import axios, {
    AxiosError,
    AxiosRequestConfig,
    InternalAxiosRequestConfig,
} from 'axios';
import { getAuthToken } from './auth';
import {
    AuthCircuitOpenError,
    getCircuitState,
    recordAuthFailure,
    recordSuccess,
} from './authCircuitBreaker';

// Retry policy (HTTP transport layer, 5xx + network errors only). 401/403
// are terminal — they go to the circuit breaker, not to retry — because the
// root cause of P525389088 was unbounded retry on auth failure.
const MAX_ATTEMPTS = 3;
const RETRY_BASE_MS = 500;
const RETRY_FACTOR = 2;
const RETRY_JITTER_RATIO = 0.5;
const RETRY_CAP_MS = 5_000;

type RetryableConfig = InternalAxiosRequestConfig & { __retryCount?: number };

/**
 * Sleep that resolves early if `signal` is aborted. The retry loop needs
 * this so a caller-set AbortSignal (e.g. the GlossaryProvider's 2 s
 * client-side timeout) actually bounds the total time, not just the
 * network attempt — a plain setTimeout sleep is not interruptible, so an
 * abort fired DURING the backoff would only land after the sleep
 * completes, blowing past the caller's deadline by up to RETRY_CAP_MS.
 */
const abortableSleep = (ms: number, signal?: AbortSignal): Promise<void> =>
    new Promise<void>((resolve) => {
        if (signal?.aborted) {
            resolve();
            return;
        }
        const timer = setTimeout(() => {
            signal?.removeEventListener('abort', onAbort);
            resolve();
        }, ms);
        const onAbort = () => {
            clearTimeout(timer);
            signal?.removeEventListener('abort', onAbort);
            resolve();
        };
        signal?.addEventListener('abort', onAbort);
    });

// HTTP methods safe to retry without side-effect risk. POST / PATCH are
// excluded: on a 5xx-after-the-write-committed scenario the server may have
// already applied the mutation but failed to respond, and a blind retry
// would duplicate the operation. Callers that need retry on non-idempotent
// methods should implement an idempotency key and handle it explicitly.
const IDEMPOTENT_METHODS = new Set(['get', 'head', 'options', 'put', 'delete']);

// Jest workers can't exit cleanly if the retry logic schedules setTimeout
// callbacks that outlive the test (any test that renders a component which
// touches the shared axios instance against jsdom's ECONNREFUSED path will
// leak timers). Default to disabled under NODE_ENV=test so test suites
// terminate deterministically. webpack inlines process.env.NODE_ENV at build
// time, so production and dev bundles still get full retry behavior.
// The two retry-behavior tests use __setRetriesDisabledForTests to flip
// this back on under fake timers while they assert the retry mechanics.
let RETRIES_DISABLED = process.env.NODE_ENV === 'test';
export const __setRetriesDisabledForTests = (disabled: boolean): void => {
    RETRIES_DISABLED = disabled;
};

export const jitteredBackoff = (attempt: number): number => {
    // Compute the ideal delay, apply jitter, THEN clamp to the cap. Clamping
    // before jitter (the previous shape) could return up to 1.5 * cap on the
    // upper end of the jitter range, violating the "5 s cap" guarantee.
    const base = RETRY_BASE_MS * Math.pow(RETRY_FACTOR, attempt);
    const jitter = base * ((Math.random() - 0.5) * 2 * RETRY_JITTER_RATIO);
    const jittered = Math.max(0, base + jitter);
    return Math.min(RETRY_CAP_MS, jittered);
};

const isRetryableError = (error: AxiosError): boolean => {
    if (RETRIES_DISABLED) {
        return false;
    }
    // Caller-driven cancellation (controller.abort(), including the
    // GlossaryProvider's 2 s client-side timeout). Retrying a canceled
    // request would just sleep the backoff and then re-reject, which
    // pushes the error state past the caller's deadline — the opposite
    // of what the caller asked for by aborting. Fail fast instead.
    //
    // `axios.isCancel` is typed as a `payload is Cancel` guard, which
    // narrows the parameter to `Cancel` on true and to `never` on false
    // when the input is already typed as `AxiosError`. Call it against
    // `unknown` so the subsequent `AxiosError` surface stays usable.
    if (axios.isCancel(error as unknown)) {
        return false;
    }
    // Only retry idempotent methods — see IDEMPOTENT_METHODS above.
    const method = (error.config?.method ?? 'get').toLowerCase();
    if (!IDEMPOTENT_METHODS.has(method)) {
        return false;
    }
    if (!error.response) {
        // Network error / no response — retryable.
        return true;
    }
    const status = error.response.status;
    // 503 often carries application meaning (e.g. maintenance mode) and must
    // surface immediately instead of being retried MAX_ATTEMPTS-1 times with
    // backoff. Short-circuit before the generic 500-599 range check.
    if (status === 503) {
        return false;
    }
    return status >= 500 && status < 600;
};

export const createAxiosInstance = (baseURL?: string) => {
    const instance = axios.create({
        baseURL: baseURL || 'https://api.gamma.learning.robotics.amazon.dev/',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    // Axios 1.x consumes request interceptors in reverse-registration order
    // (requestInterceptorChain.unshift in Axios.js), so the LAST .use(...)
    // call runs FIRST at request time. We register the auth-token interceptor
    // first so it is at the tail of the chain, and the circuit-check
    // interceptor last so it runs at the head — a loop that is already
    // tripping 401s must not keep calling fetchAuthSession (-> Cognito /
    // Amplify) for every doomed request.
    instance.interceptors.request.use(async (config) => {
        config.headers.Authorization = await getAuthToken();
        return config;
    });

    instance.interceptors.request.use(async (config) => {
        if (getCircuitState().open) {
            throw new AuthCircuitOpenError();
        }
        return config;
    });

    // Response interceptor: feed success/auth-failure into the circuit breaker
    // and retry transient 5xx / network errors with jittered exponential backoff.
    instance.interceptors.response.use(
        (response) => {
            recordSuccess();
            return response;
        },
        async (error: unknown) => {
            if (
                error instanceof Error &&
                (error as AuthCircuitOpenError).isAuthCircuitOpenError
            ) {
                return Promise.reject(error);
            }
            const axiosErr = error as AxiosError;

            const status = axiosErr.response?.status;
            if (status === 401 || status === 403) {
                recordAuthFailure(status);
                return Promise.reject(axiosErr);
            }

            const config = axiosErr.config as RetryableConfig | undefined;
            if (!config || !isRetryableError(axiosErr)) {
                return Promise.reject(axiosErr);
            }

            const attempt = config.__retryCount ?? 0;
            if (attempt >= MAX_ATTEMPTS - 1) {
                return Promise.reject(axiosErr);
            }

            // Honor a caller-set AbortSignal around the backoff sleep.
            // Checking before AND after the sleep covers two races:
            //   1. The caller already aborted before we entered retry
            //      (short-circuit immediately so we don't sleep at all).
            //   2. The caller aborts during the sleep (abortableSleep
            //      resolves early; we must not then issue a new request
            //      — reject with the original transport error).
            const signal = (
                config as RetryableConfig & { signal?: AbortSignal }
            ).signal;
            if (signal?.aborted) {
                return Promise.reject(axiosErr);
            }
            config.__retryCount = attempt + 1;
            await abortableSleep(jitteredBackoff(attempt), signal);
            if (signal?.aborted) {
                return Promise.reject(axiosErr);
            }
            return instance.request(config);
        }
    );

    return instance;
};

// Convenience re-exports so callers importing from the axios module can see
// the circuit-breaker surface without pulling in the breaker module directly.
export { AuthCircuitOpenError } from './authCircuitBreaker';
export type { AxiosRequestConfig };
